<script lang="ts">
  import { guidesPath, guideChecklistPath } from "../../../../support/links";
  import { page } from "$app/stores";

  import Sprite from "../../../../components/sprite.svelte";
  import Booklet from "../../../../components/guides/booklet.svelte";
  import GuidePage from "../../../../components/guides/guide-page.svelte";
  import GuideIndex from "../../../../components/guides/guide-index.svelte";
  import GuideChecklist from "../../../../components/guides/guide-checklist.svelte";

  export let data;

  $: guide = data.guide;
  $: spots = data.guide.spots;
  $: pageId = $page.url.searchParams.get("id") || "cover";
  $: spot = spots.find((spot) => spot.id == pageId);

  $: pages = ["cover", guide.includeIndexPage ? "index" : null]
    .concat(spots.map((s) => s.id))
    .filter((i) => i != null);

  $: position = Math.max(0, pages.indexOf(pageId));

  function pageAt(pageId: string, delta: number) {
    let nextIndex = pages.indexOf(pageId) + delta;

    return pages[nextIndex] || pageId;
  }

  let showCompletionModal = false;

  function toggleCompletionModal() {
    showCompletionModal = !showCompletionModal;
  }

  function closeCompletionModal() {
    showCompletionModal = false;
  }
</script>

{#key pageId}
  <div class="Frame">
    {#if pageId == "cover"}
      <div class="BookletFrame">
        <Booklet {guide} />
      </div>
    {:else if pageId == "index"}
      <GuideIndex {guide} />
    {:else if spot}
      <GuidePage {spot} {guide} />
    {/if}
  </div>
{/key}

<footer>
  <a href={data.returnRoute} class="Action">
    <Sprite icon="icon-return" size="24" />
    <span class="sr-only">Return to guides</span>
  </a>

  <div href={guidesPath({ start: guide.id })} class="Action Stepper">
    <a href={guideChecklistPath(guide, { id: pageAt(pageId, -1) })} disabled={pageId == "cover"}>
      <Sprite icon="icon-guide-prev" size="24" /></a
    >
    <span>{position + 1} / {pages.length}</span>
    <a
      href={guideChecklistPath(guide, { id: pageAt(pageId, 1) })}
      disabled={pageId == spots[spots.length - 1]?.id}
    >
      <Sprite icon="icon-guide-next" size="24" /></a
    >
  </div>

  <button
    href={guideChecklistPath(guide, { id: "index" })}
    class="Action"
    on:click={toggleCompletionModal}
  >
    <Sprite icon="icon-hamburger" size="24" />
    <span class="sr-only">View Index</span>
  </button>
</footer>

{#if showCompletionModal}
  <GuideChecklist {guide} on:close={closeCompletionModal} />
{/if}

<style>
  .Frame {
    flex: 1 1 auto;
    overflow-y: auto;
    padding-bottom: max(env(safe-area-inset-bottom), 16px);
    padding-left: max(env(safe-area-inset-right), 16px);
    padding-right: max(env(safe-area-inset-left), 16px);
    padding-top: max(env(safe-area-inset-top), 16px);
    position: relative;
  }

  .BookletFrame {
    align-items: stretch;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    padding: 16px;
    position: absolute;
    top: 0;
    width: 100%;
  }

  footer {
    --color-tooling: #d3d2ca;
    align-items: center;
    border-width: 1px 0;
    border: 1px solid var(--color-tooling);
    display: flex;
    flex: 0 1;
    grid-gap: 15px;
    justify-content: space-between;
    padding: 15px;
  }

  .Action {
    align-items: center;
    aspect-ratio: 1 / 1;
    flex: 0 0;
    font-size: 14px;
    background: white;
    border-radius: 24px;
    border: 1px solid var(--color-tooling);
    color: inherit;
    display: flex;
    justify-content: center;
    grid-gap: 12px;
    padding: 12px;
  }

  .Stepper {
    aspect-ratio: unset;
    white-space: nowrap;
  }

  .Stepper a {
    transition: 0.3s opacity;
  }

  .Stepper a[disabled="true"] {
    opacity: 0.15;
  }
</style>
